import React from 'react';

import dynamic from 'next/dynamic';
import Head from 'next/head';

import { MarketplaceAPI } from 'revibe-api';
import { useTranslation } from 'i18n/hooks';
import Marquee from 'react-fast-marquee';

import { ErrorBoundary } from 'modules/common/components/ErrorBoundary';
import { Footer } from 'modules/common/components/Footer';
import { useBoolean } from 'modules/common/hooks';

import { BlogNavBar } from './BlogNavBar';
import { BlogPanel } from './BlogPanel';

const NextNProgress = dynamic(() => import('nextjs-progressbar'));

type Props = {
  title: string;
  description?: string | undefined | null;
  keywords?: string | undefined;
  children: React.ReactNode;
  categories?: MarketplaceAPI.BlogCategory[];
  shouldPostfixTitle?: boolean;
  noIndex?: boolean;
};

export const BlogPage = ({
  title,
  description,
  noIndex,
  children,
  categories = [],
  shouldPostfixTitle = true,
  keywords = '',
}: Props) => {
  const { tu, language } = useTranslation();
  const [isPanelOpen, openPanel, closePanel] = useBoolean();

  const meta = [
    {
      name: 'og:type',
      content: 'article',
    },
    {
      name: 'og:locale',
      content: language,
    },
    {
      name: 'description',
      content: description || tu('blog.description', 'seo'),
    },
    {
      name: 'og:title',
      content: title,
    },
    {
      name: 'og:description',
      content: description || tu('blog.description', 'seo'),
    },
    {
      name: 'keywords',
      content: `${tu('blog.keywords', 'seo')},${keywords}`,
    },
  ];
  if (noIndex) {
    meta.push({
      name: 'robots',
      content: 'noindex',
    });
  }
  return (
    <>
      <Head>
        <title>{shouldPostfixTitle ? `${title} | Stain` : title}</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        {meta.map((metaEntry) => (
          <meta
            key={metaEntry.name}
            name={metaEntry.name}
            content={metaEntry.content}
          />
        ))}
      </Head>
      <div className="relative max-w-[100vw]" suppressHydrationWarning={true}>
        <div className="h-[4.5rem] md:h-[4.75rem]" />
        <NextNProgress
          color="var(--black)"
          startPosition={0.3}
          stopDelayMs={200}
          height={4}
          showOnShallow={true}
          options={{ showSpinner: false }}
        />
        <ErrorBoundary>
          <main className="box-border">{children}</main>
        </ErrorBoundary>
        <Footer />
        <div className="fixed top-0">
          {categories.length >= 0 && (
            <Marquee
              gradient={false}
              loop={0}
              direction="right"
              className="bg-green-fluo z-0 cursor-pointer"
            >
              {[
                0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17,
                18, 19, 20,
              ].map((i) => (
                <p
                  key={i}
                  className="flex py-1 pr-4 text-sm font-light uppercase text-black"
                >
                  We investigate the world of upcycling, sustainability, design,
                  technology and much more | Curated by Revibe.
                  <span />
                </p>
              ))}
            </Marquee>
          )}
          <BlogNavBar toggleMenu={openPanel} />
        </div>
        <BlogPanel
          categories={categories}
          isOpen={isPanelOpen}
          onClose={closePanel}
        />
      </div>
    </>
  );
};
